/**
 * Playoff odds for the live season: simulate the rest of the regular season
 * many times, seed each simulated league the way Yahoo does, and count how
 * often each team gets in (and wins it all).
 *
 * The model is deliberately plain. A team's weekly score is drawn from a bell
 * curve around its own average so far, pulled toward the league average while
 * it has few games, with the same spread for everyone. Weekly scores swing by
 * about 28 points and a team's own average barely beats the league average as
 * a predictor (30.4 points of error against 29.6 shrunk, over 1,122 games from
 * 2016 to 2025), so anything fancier would claim precision the data doesn't
 * have. Yahoo's projections for future weeks are ignored: they swing wildly
 * (bye weeks, injuries) and only the schedule is trustworthy that far out.
 *
 * Rules, checked against every 12-team season in the history: the top
 * `playoffTeams` by record (a tie is half a win), then points scored, make
 * it; with eight teams the bracket is fixed with no reseeding: 1v8, 2v7, 3v6,
 * 4v5, then the winner of 1v8 against the winner of 4v5, and the winner of
 * 2v7 against the winner of 3v6, then the final. Title odds are only
 * computed for an eight-team field.
 */

import type { LeagueHistory } from "../history";

export interface PlayoffOddsOptions {
  /** Simulated seasons. More is smoother and slower. */
  simulations?: number;
  /** Standard deviation of a team's score around its expected score, in points. */
  scoreSd?: number;
}

export interface TeamOdds {
  personId: string;
  /** Chance of finishing in the playoff field, 0 to 1. */
  playoffOdds: number;
  /** Chance of winning the championship; null unless the field is eight teams. */
  titleOdds: number | null;
}

export interface PlayoffOdds {
  season: number;
  /** The week in progress (the first regular-season week not fully played). */
  week: number;
  /** Regular-season weeks left, counting the week in progress. */
  weeksRemaining: number;
  playoffTeams: number;
  simulations: number;
  /** Likeliest to make it first, then likeliest to win it all. */
  teams: TeamOdds[];
}

/** Weeks of a team's own scores that count as much as the league average when estimating its strength. */
const PRIOR_WEEKS = 4;
/** Prediction error of the shrunk own-average predictor over 2016-2025 (see the module comment). */
const DEFAULT_SCORE_SD = 29.5;
const DEFAULT_SIMULATIONS = 10_000;
const BRACKET_SIZE = 8;

/**
 * Null when there's nothing to forecast: the season is over, the playoffs have
 * started, Yahoo didn't report the playoff rules, or the schedule for the
 * weeks ahead hasn't been recorded.
 */
export function simulatePlayoffOdds(history: LeagueHistory, options: PlayoffOddsOptions = {}): PlayoffOdds | null {
  const season = history.seasons.at(-1);
  if (!season || season.isFinished || season.currentWeek === null) return null;
  const { playoffStartWeek, playoffTeams, currentWeek } = season;
  if (!playoffStartWeek || !playoffTeams || playoffTeams < 1) return null;
  const lastRegularWeek = playoffStartWeek - 1;
  if (currentWeek > lastRegularWeek) return null;
  // The weeks after this one come from the stored schedule; without it there's nothing to simulate.
  if (currentWeek < lastRegularWeek && !season.schedule) return null;

  const personOf = (teamKey: string) => history.personByTeamKey[teamKey];
  const regular = season.games.filter((g) => g.gameType === "regular");

  // Everyone with a team this season, with where they stand now.
  // One entry per person: two teams resolved to one manager is a flagged data problem, and giving each its own
  // slot would split that manager's games across entries while counting them twice in the playoff field.
  const ids = [...new Set(season.teams.map((t) => personOf(t.teamKey)).filter((id): id is string => id !== undefined))];
  if (ids.length < 2) return null;
  const index = new Map(ids.map((id, i) => [id, i]));
  const wins = ids.map(() => 0);
  const pointsFor = ids.map(() => 0);
  const gamesPlayed = ids.map(() => 0);
  for (const game of regular) {
    for (const [mine, theirs] of [
      [game.sides[0], game.sides[1]],
      [game.sides[1], game.sides[0]],
    ] as const) {
      const i = index.get(personOf(mine.teamKey) ?? "");
      if (i === undefined) continue;
      wins[i]! += mine.points > theirs.points ? 1 : mine.points === theirs.points ? 0.5 : 0;
      pointsFor[i]! += mine.points;
      gamesPlayed[i]!++;
    }
  }

  const strength = strengths(ids.length, pointsFor, gamesPlayed, history, regular.length);
  if (!strength) return null;

  // The games still to play: this week's unfinished slate, then each later scheduled week.
  const remaining: [number, number][] = [];
  const weeksWithGames = new Set<number>();
  const addPairing = (teamKeys: readonly [string, string], week: number) => {
    const a = index.get(personOf(teamKeys[0]) ?? "");
    const b = index.get(personOf(teamKeys[1]) ?? "");
    if (a !== undefined && b !== undefined && a !== b) {
      remaining.push([a, b]);
      weeksWithGames.add(week);
    }
  };
  for (const game of season.upcoming) if (game.gameType === "regular" && game.week <= lastRegularWeek) addPairing(game.teamKeys, game.week);
  for (const game of season.schedule ?? []) {
    if (game.gameType === "regular" && game.week > currentWeek && game.week <= lastRegularWeek) addPairing(game.teamKeys, game.week);
  }

  const simulations = options.simulations ?? DEFAULT_SIMULATIONS;
  const sd = options.scoreSd ?? DEFAULT_SCORE_SD;
  const random = normalSource(season.season * 100 + currentWeek);
  const made = ids.map(() => 0);
  const titles = ids.map(() => 0);
  const bracket = Math.min(playoffTeams, ids.length);

  for (let sim = 0; sim < simulations; sim++) {
    const simWins = wins.slice();
    const simPoints = pointsFor.slice();
    for (const [a, b] of remaining) {
      const scoreA = strength[a]! + sd * random();
      const scoreB = strength[b]! + sd * random();
      simPoints[a]! += scoreA;
      simPoints[b]! += scoreB;
      simWins[scoreA > scoreB ? a : b]!++;
    }

    // Yahoo's order: record, then points scored.
    const seeds = ids.map((_, i) => i).sort((x, y) => simWins[y]! - simWins[x]! || simPoints[y]! - simPoints[x]!);
    for (let s = 0; s < bracket; s++) made[seeds[s]!]!++;
    if (playoffTeams === BRACKET_SIZE && ids.length >= BRACKET_SIZE) {
      titles[playBracket(seeds, strength, sd, random)]!++;
    }
  }

  const teams: TeamOdds[] = ids
    .map((personId, i) => ({
      personId,
      playoffOdds: made[i]! / simulations,
      titleOdds: playoffTeams === BRACKET_SIZE ? titles[i]! / simulations : null,
    }))
    .sort((a, b) => b.playoffOdds - a.playoffOdds || (b.titleOdds ?? 0) - (a.titleOdds ?? 0) || a.personId.localeCompare(b.personId));

  return {
    season: season.season,
    week: currentWeek,
    // Weeks that still have a game to play, so it reaches 0 once the last regular week's games are all final
    // (even before Yahoo moves on to the playoffs), which is when the field is settled.
    weeksRemaining: weeksWithGames.size,
    playoffTeams,
    simulations,
    teams,
  };
}

/**
 * Each team's expected weekly score: its own average so far, weighted by
 * games played against `PRIOR_WEEKS` of the league average. The league average
 * is this season's so far, or every regular game in the history before any
 * are played. Null when there is no score to go on at all.
 */
function strengths(
  count: number,
  pointsFor: readonly number[],
  gamesPlayed: readonly number[],
  history: LeagueHistory,
  seasonGames: number,
): number[] | null {
  let leagueMean: number | null = null;
  if (seasonGames > 0) {
    leagueMean = pointsFor.reduce((sum, p) => sum + p, 0) / gamesPlayed.reduce((sum, g) => sum + g, 0);
  } else {
    const all = history.seasons.flatMap((s) => s.games.filter((g) => g.gameType === "regular").flatMap((g) => g.sides.map((side) => side.points)));
    if (all.length > 0) leagueMean = all.reduce((sum, p) => sum + p, 0) / all.length;
  }
  if (leagueMean === null) return null;
  return Array.from({ length: count }, (_, i) => {
    const n = gamesPlayed[i]!;
    return n === 0 ? leagueMean : (pointsFor[i]! + PRIOR_WEEKS * leagueMean) / (n + PRIOR_WEEKS);
  });
}

/** Plays the fixed eight-team bracket; returns the champion's index. `seeds` lists team indexes best seed first. */
function playBracket(seeds: readonly number[], strength: readonly number[], sd: number, random: () => number): number {
  const beats = (a: number, b: number) => strength[a]! + sd * random() > strength[b]! + sd * random();
  const winner = (a: number, b: number) => (beats(a, b) ? a : b);
  const s = (seed: number) => seeds[seed - 1]!;
  const top = winner(winner(s(1), s(8)), winner(s(4), s(5)));
  const bottom = winner(winner(s(2), s(7)), winner(s(3), s(6)));
  return winner(top, bottom);
}

/** A repeatable stream of standard normal draws (mulberry32 uniform generator, Box-Muller transform). */
function normalSource(seed: number): () => number {
  let state = seed >>> 0;
  const uniform = () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  let spare: number | null = null;
  return () => {
    if (spare !== null) {
      const value = spare;
      spare = null;
      return value;
    }
    // 1 - uniform() keeps the log's argument in (0, 1].
    const radius = Math.sqrt(-2 * Math.log(1 - uniform()));
    const angle = 2 * Math.PI * uniform();
    spare = radius * Math.sin(angle);
    return radius * Math.cos(angle);
  };
}
